import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { EnhancedGameCreationModal } from '@/components/games/EnhancedGameCreationModal';
import { useAuthenticatedUser } from '@/hooks/useAuthenticatedUser';
import { GameManagementIntegration } from '@/components/games/GameManagementIntegration';
import Header from '@/components/Header';
import { Plus, Trophy, Calendar, Users, Info } from 'lucide-react';
import PermissionGate from '@/components/rbac/PermissionGate';

export default function CreateGame() {
  const { user } = useAuthenticatedUser();
  const [modalOpen, setModalOpen] = useState(false);

  const handleGameCreated = (gameId?: string) => {
    setModalOpen(false);
    // The EnhancedGameCreationModal will handle redirection to unified form
  };

  return (
    <GameManagementIntegration>
      <Header />
      <div className="min-h-screen bg-background pt-20">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto space-y-8">
            <PermissionGate 
              permission="manage_games"
              fallback={
                <div className="text-center space-y-6">
                  <h1 className="text-4xl font-bold text-foreground flex items-center justify-center gap-3">
                    <Trophy className="w-10 h-10 text-primary" />
                    Create New Game
                  </h1>
                  <Alert className="border-orange-500/50 bg-orange-500/10 max-w-2xl mx-auto">
                    <Info className="h-4 w-4 text-orange-400" />
                    <AlertDescription className="text-orange-300">
                      <strong>Game Creation Restricted:</strong> Only Team Owners, Coaches, and Venues can create games. 
                      This ensures proper oversight and safety protocols. If you need a game created, please contact 
                      an authorized user in your area.
                    </AlertDescription>
                  </Alert>
                </div>
              }
            >
              {/* Header */}
              <div className="text-center space-y-4">
                <h1 className="text-4xl font-bold text-foreground flex items-center justify-center gap-3">
                  <Trophy className="w-10 h-10 text-primary" />
                  Create New Game
                </h1>
                <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                  Set up your next game with teams, venue, and all the details
                </p>
                <Alert className="border-blue-500/50 bg-blue-500/10 max-w-2xl mx-auto">
                  <Info className="h-4 w-4 text-blue-400" />
                  <AlertDescription className="text-blue-300">
                    <strong>Who can create games:</strong> Team Owners, Coaches, and Venues only. 
                    This ensures proper safety oversight and game management.
                  </AlertDescription>
                </Alert>
              </div>

              {/* Quick Actions */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setModalOpen(true)}>
                  <CardHeader className="text-center">
                    <Calendar className="w-12 h-12 text-primary mx-auto mb-2" />
                    <CardTitle>Schedule Game</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground text-center">
                      Create a new scheduled game with specific teams and venue
                    </p>
                  </CardContent>
                </Card>

                <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setModalOpen(true)}>
                  <CardHeader className="text-center">
                    <Users className="w-12 h-12 text-primary mx-auto mb-2" />
                    <CardTitle>Open Game</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground text-center">
                      Create an open game that teams can join with a code
                    </p>
                  </CardContent>
                </Card>

                <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setModalOpen(true)}>
                  <CardHeader className="text-center">
                    <Plus className="w-12 h-12 text-primary mx-auto mb-2" />
                    <CardTitle>Quick Setup</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground text-center">
                      Fast game creation with minimal setup required
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Main Action */}
              <div className="text-center">
                <EnhancedGameCreationModal
                  userId={user?.id}
                  onGameCreated={handleGameCreated}
                  trigger={
                    <Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90">
                      <Plus className="w-5 h-5 mr-2" />
                      Start Creating Game
                    </Button>
                  }
                />
              </div>
            </PermissionGate>
          </div>
        </div>
      </div>
    </GameManagementIntegration>
  );
}